import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Globe, Mail, Calendar, ShieldCheck, ExternalLink, Users, UserPlus, UserCheck, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import GlassBoxBadge from '@/components/GlassBoxBadge';
import GlassBoxWidget from '@/components/GlassBoxWidget';
import ProjectCard from '@/components/ProjectCard';
import { getStore, setStore } from '@/utils/localStore';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const mockNGOActivity = [
  { id: 1, type: 'update', text: 'Published a new project update for Clean Water for Kigali', time: '3 hours ago' },
  { id: 2, type: 'milestone', text: 'Reached 80% funding on Solar-Powered Clinics', time: '1 day ago' },
  { id: 3, type: 'new_project', text: 'Launched a new project: Youth Mentorship Nairobi', time: '3 days ago' },
  { id: 4, type: 'thank', text: 'Thanked 12 donors for monthly contributions', time: '5 days ago' },
  { id: 5, type: 'report', text: 'Published Q4 2025 Impact Report', time: '1 week ago' },
];

function FollowButton({ followerCount, ngoSlug }) {
  const [state, setState] = useState('idle');
  const handleClick = () => {
    const newState = state === 'idle' ? 'following' : 'idle';
    setState(newState);
    toast.success(newState === 'following' ? 'You are now following this organization!' : 'Unfollowed organization');
    const follows = getStore('follows');
    const followerId = 'sarah-chen';
    const followingId = ngoSlug || 'ngo';
    const idx = follows.findIndex(f => f.followerId === followerId && f.followingId === followingId && f.followingType === 'ngo');
    if (idx >= 0) {
      follows.splice(idx, 1);
    } else {
      follows.push({ followerId, followingId, followingType: 'ngo', createdAt: new Date().toISOString() });
    }
    setStore('follows', follows);
  };
  return (
    <div className="flex items-center gap-3">
      <Button
        onClick={handleClick}
        variant={state === 'following' ? 'outline' : 'default'}
        className={`rounded-full h-10 text-sm gap-2 ${state === 'following' ? 'border-[#7C3AED] text-[#7C3AED]' : 'bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white border-0 hover:shadow-lg'}`}
        data-testid="follow-ngo-btn"
      >
        {state === 'following' ? <><UserCheck className="w-4 h-4" /> Following</> : <><UserPlus className="w-4 h-4" /> Follow</>}
      </Button>
      <span className="text-sm text-gray-500 font-body">{(followerCount || 1247).toLocaleString()} followers</span>
    </div>
  );
}

const activityTypeStyles = {
  milestone: { bg: 'bg-[#10B981]/10', icon: ShieldCheck, iconClass: 'w-4 h-4 text-[#10B981]' },
  new_project: { bg: 'bg-[#7C3AED]/10', icon: ExternalLink, iconClass: 'w-4 h-4 text-[#7C3AED]' },
  thank: { bg: 'bg-rose-50', icon: Users, iconClass: 'w-4 h-4 text-rose-500' },
  report: { bg: 'bg-[#2563EB]/10', icon: Globe, iconClass: 'w-4 h-4 text-[#2563EB]' },
};
const defaultActivityStyle = { bg: 'bg-gray-50', icon: Bell, iconClass: 'w-4 h-4 text-gray-500' };

function NGOActivityFeed() {
  return (
    <motion.section {...fadeInUp} data-testid="ngo-activity-feed">
      <h2 className="text-2xl font-heading font-semibold text-[#111827] mb-6">Recent Activity</h2>
      <div className="space-y-4">
        {mockNGOActivity.map(a => {
          const style = activityTypeStyles[a.type] || defaultActivityStyle;
          const Icon = style.icon;
          return (
            <div key={a.id} className="flex items-start gap-3 bg-white rounded-xl p-4 shadow-[0_4px_15px_rgb(0,0,0,0.03)] border border-gray-100">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${style.bg}`}>
                <Icon className={style.iconClass} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#4B5563] font-body">{a.text}</p>
                <p className="text-xs text-gray-400 mt-1">{a.time}</p>
              </div>
            </div>
          );
        })}
      </div>
    </motion.section>
  );
}

export default function NGOProfilePage() {
  const { slug } = useParams();
  const [ngo, setNgo] = useState(null);
  const [projects, setProjects] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [ngosRes, projRes] = await Promise.all([
          fetch('/data/ngos.json').then(r => r.json()),
          fetch('/data/projects.json').then(r => r.json()),
        ]);
        const foundNgo = ngosRes.find(n => n.slug === slug);
        if (!foundNgo) {
          setNgo({ error: 'NGO not found' });
          setLoading(false);
          return;
        }
        const ngoWithFollowers = { ...foundNgo, followerCount: foundNgo.followerCount || 1247 };
        setNgo(ngoWithFollowers);
        const ngoProjects = projRes.filter(p => p.ngoId === foundNgo.id);
        setProjects(ngoProjects);
        if (foundNgo.id) {
          const txAll = await fetch('/data/transactions.json').then(r => r.json());
          const ngoTx = txAll.filter(t => t.ngoId === foundNgo.id).slice(0, 10);
          setTransactions(ngoTx);
        }
      } catch (err) {
        toast.error('Failed to load organization');
      } finally { setLoading(false); }
    };
    fetchData();
  }, [slug]);

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 py-20 text-center">
      <div className="animate-pulse space-y-4">
        <div className="h-20 w-20 bg-gray-100 rounded-full mx-auto" />
        <div className="h-8 bg-gray-100 rounded w-1/3 mx-auto" />
        <div className="h-4 bg-gray-100 rounded w-1/4 mx-auto" />
      </div>
    </div>
  );

  if (!ngo || ngo.error) return (
    <div className="max-w-7xl mx-auto px-4 py-20 text-center">
      <h2 className="font-heading text-2xl font-semibold text-[#111827] mb-2">NGO not found</h2>
      <Link to="/explore"><Button variant="outline" className="rounded-full">Back to Explore</Button></Link>
    </div>
  );

  const initials = ngo.name?.split(' ').map(w => w[0]).join('').slice(0, 2);
  const verificationBadges = ['501(c)(3) Verified', 'HumaDash Integrated', 'KYB Complete'];
  const teamMembers = ngo.teamMembers || [];
  const allMilestones = projects.flatMap(p => (p.milestones || []).map(m => ({ ...m, projectTitle: p.title, projectSlug: p.slug }))).sort((a, b) => new Date(b.date) - new Date(a.date));

  const stats = [
    { label: 'Total Raised', value: `$${ngo.totalFunding?.toLocaleString()}`, txHash: ngo.txHash },
    { label: 'Active Projects', value: ngo.activeProjects || projects.filter(p => p.status !== 'complete').length, txHash: ngo.txHash },
    { label: 'Beneficiaries Served', value: (ngo.totalBeneficiaries || 0).toLocaleString(), txHash: ngo.txHash },
    { label: 'Volunteer Hours', value: '2,450+', txHash: ngo.txHash },
  ];

  const mostActiveProject = projects.find(p => p.status !== 'complete') || projects[0];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="ngo-profile-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          <Link to="/explore">
            <Button variant="ghost" className="text-gray-500 hover:text-[#111827] mb-6 -ml-2 text-sm font-body" data-testid="back-to-explore">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Explore
            </Button>
          </Link>
          <motion.div className="flex flex-col md:flex-row items-start gap-6" {...fadeInUp}>
            <Avatar className="w-20 h-20">
              <AvatarFallback className="bg-gradient-to-br from-[#7C3AED] to-[#2563EB] text-white font-heading font-semibold text-2xl">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-[#111827] tracking-tight mb-2">{ngo.name}</h1>
              <div className="flex items-center gap-4 text-sm text-gray-500 font-body mb-3">
                <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {ngo.location}</span>
                <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Founded {ngo.yearFounded}</span>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {verificationBadges.map(b => (
                  <span key={b} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#10B981]/10 text-[#10B981] text-xs font-medium font-body">
                    <ShieldCheck className="w-3 h-3" /> {b}
                  </span>
                ))}
              </div>
              <FollowButton followerCount={ngo.followerCount} ngoSlug={slug} />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((s) => (
              <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-center">
                <p className="text-2xl sm:text-3xl font-heading font-semibold text-[#111827]">{s.value}</p>
                <div className="flex items-center justify-center gap-2 mt-1">
                  <p className="text-xs text-gray-500 font-body">{s.label}</p>
                  <GlassBoxBadge txHash={s.txHash} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1 space-y-12">
            {/* About */}
            <motion.section {...fadeInUp} data-testid="ngo-about">
              <h2 className="text-2xl font-heading font-semibold text-[#111827] mb-4">About</h2>
              <p className="text-[#4B5563] leading-relaxed mb-4 font-body">{ngo.description}</p>
              <div className="bg-gradient-to-r from-[#7C3AED]/5 to-[#2563EB]/5 rounded-xl p-5">
                <h3 className="font-heading font-semibold text-sm text-[#111827] mb-2">Mission Statement</h3>
                <p className="text-sm text-[#4B5563] leading-relaxed italic font-body">{ngo.mission}</p>
              </div>
            </motion.section>

            {/* Activity Feed */}
            <NGOActivityFeed />

            {/* Projects */}
            <motion.section {...fadeInUp} data-testid="ngo-projects">
              <h2 className="text-2xl font-heading font-semibold text-[#111827] mb-6">Projects ({projects.length})</h2>
              {projects.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {projects.map(p => <ProjectCard key={p.id} project={p} />)}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8 font-body">No projects yet.</p>
              )}
            </motion.section>

            {/* Impact Timeline */}
            <motion.section {...fadeInUp} data-testid="ngo-timeline">
              <h2 className="text-2xl font-heading font-semibold text-[#111827] mb-6">Impact Timeline</h2>
              <div className="space-y-0">
                {allMilestones.slice(0, 10).map((m, i) => (
                  <div key={`${m.title}-${m.date}`} className="flex gap-4 pb-5 last:pb-0">
                    <div className="flex flex-col items-center">
                      <div className={`w-3 h-3 rounded-full shrink-0 mt-1.5 ${m.completed ? 'bg-[#10B981]' : 'bg-gray-200'}`} />
                      {i < Math.min(allMilestones.length, 10) - 1 && <div className="w-0.5 flex-1 bg-gray-100 mt-1" />}
                    </div>
                    <div className="pb-1">
                      <p className="font-heading font-medium text-[#111827] text-sm">{m.title}</p>
                      <p className="text-xs text-gray-500 font-body">
                        {m.date} &mdash; <Link to={`/project/${m.projectSlug}`} className="text-[#7C3AED] hover:text-[#2563EB] transition-colors">{m.projectTitle}</Link>
                      </p>
                      {m.completed && <GlassBoxBadge label="Verified" txHash={ngo.txHash} />}
                    </div>
                  </div>
                ))}
              </div>
            </motion.section>

            {/* Team */}
            {teamMembers.length > 0 && (
              <motion.section {...fadeInUp} data-testid="ngo-team">
                <h2 className="text-2xl font-heading font-semibold text-[#111827] mb-6">Team</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {teamMembers.map((member, i) => (
                    <div key={i} className="bg-white rounded-xl p-5 shadow-[0_4px_15px_rgb(0,0,0,0.03)] border border-gray-100">
                      <div className="flex items-center gap-3 mb-3">
                        <Avatar className="w-11 h-11">
                          <AvatarFallback className="bg-gradient-to-br from-[#7C3AED]/20 to-[#2563EB]/20 text-[#7C3AED] font-heading font-semibold text-sm">
                            {member.initials}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-heading font-semibold text-[#111827] text-sm">{member.name}</p>
                          <p className="text-xs text-gray-500 font-body">{member.role}</p>
                        </div>
                      </div>
                      <p className="text-xs text-[#4B5563] leading-relaxed font-body">{member.bio}</p>
                    </div>
                  ))}
                </div>
              </motion.section>
            )}

            {/* Contact */}
            <motion.section {...fadeInUp} data-testid="ngo-contact">
              <h2 className="text-2xl font-heading font-semibold text-[#111827] mb-4">Contact</h2>
              <div className="bg-white rounded-xl p-6 shadow-[0_4px_15px_rgb(0,0,0,0.03)] border border-gray-100">
                <div className="space-y-3 mb-5">
                  {ngo.email && (
                    <div className="flex items-center gap-2 text-sm text-[#4B5563] font-body">
                      <Mail className="w-4 h-4 text-gray-400" /> {ngo.email}
                    </div>
                  )}
                  {ngo.website && (
                    <div className="flex items-center gap-2 text-sm font-body">
                      <Globe className="w-4 h-4 text-gray-400" />
                      <a href={ngo.website} target="_blank" rel="noopener noreferrer" className="text-[#7C3AED] hover:text-[#2563EB] transition-colors">{ngo.website}</a>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-[#4B5563] font-body">
                    <MapPin className="w-4 h-4 text-gray-400" /> {ngo.location}
                  </div>
                </div>
                {mostActiveProject && (
                  <Link to={`/project/${mostActiveProject.slug}`}>
                    <Button className="w-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white rounded-full border-0 hover:shadow-lg transition-all" data-testid="support-org-btn">
                      Support This Organization
                    </Button>
                  </Link>
                )}
              </div>
            </motion.section>
          </div>

          {/* Sidebar */}
          <div className="hidden lg:block w-80 shrink-0">
            <div className="sticky top-24 space-y-5">
              <GlassBoxWidget transactions={transactions} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
